"use client";

import Link from 'next/link';
import Image from 'next/image';
import CartButton from './CartButton';
import { useSession, signOut } from 'next-auth/react';
import { useState, useEffect } from 'react';
import { fetchWithRetry } from '@/lib/fetchWithRetry';

export default function Header() {
  const { data: session, status } = useSession();
  const [showDropdown, setShowDropdown] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  useEffect(() => {
    async function fetchCategories() {
      try {
        const data = await fetchWithRetry('/api/categories');
        if (data.success) {
          setCategories(data.data || []);
        } else {
          throw new Error(data.error || 'Failed to fetch categories');
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    }

    fetchCategories();
  }, []);

  const handleSignOut = async () => {
    await signOut({ redirect: true, callbackUrl: '/' });
  };

  return (
    <header className="bg-primary-800 shadow-lg">
      <div className="container mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          <Link href="/" className="flex items-center space-x-3 group">
            <Image
              src="/images/Logo.png"
              alt="Digital Store Logo"
              width={50}
              height={50}
              className="rounded-sm transition-transform group-hover:scale-105"
            />
            <span className="text-2xl font-bold text-white group-hover:text-yellow-300 transition-colors">
              EasyEDITools Store
            </span>
          </Link>

          <nav className="hidden md:flex items-center space-x-8">
            <Link
              href="/"
              className="text-white hover:text-yellow-300 transition-colors duration-200 font-medium"
            >
              Home
            </Link>
            <Link
              href="/products"
              className="text-white hover:text-yellow-300 transition-colors duration-200 font-medium"
            >
              Products
            </Link>
            <div className="relative">
              <button
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className="flex items-center space-x-1 text-white hover:text-yellow-300 transition-colors duration-200 font-medium"
              >
                <span>Categories</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 transition-transform duration-200 ${showCategoryDropdown ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showCategoryDropdown && (
                <div className="absolute left-0 mt-2 w-56 bg-primary-800 rounded-md shadow-xl py-2 z-10 border border-primary-700">
                  <Link
                    href="/products"
                    className="block px-4 py-2.5 text-sm text-white hover:bg-primary-700 transition-colors duration-200 font-medium"
                    onClick={() => setShowCategoryDropdown(false)}
                  >
                    All Categories
                  </Link>
                  {categories.map((category) => (
                    <Link
                      key={category._id}
                      href={`/products?category=${category.name}`}
                      className="block px-4 py-2.5 text-sm text-white hover:bg-primary-700 transition-colors duration-200 font-medium"
                      onClick={() => setShowCategoryDropdown(false)}
                    >
                      {category.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <Link
              href="/about"
              className="text-white hover:text-yellow-300 transition-colors duration-200 font-medium"
            >
              About
            </Link>
            <Link
              href="/contact"
              className="text-white hover:text-yellow-300 transition-colors duration-200 font-medium"
            >
              Contact
            </Link>
          </nav>

          <div className="flex items-center space-x-6">
            <CartButton />

            {status === 'authenticated' ? (
              <div className="relative">
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="hidden md:flex items-center space-x-2 text-white hover:text-yellow-300 transition-colors duration-200 font-medium"
                >
                  <span>My Account</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showDropdown && (
                  <div className="absolute right-0 mt-2 w-56 bg-primary-800 rounded-md shadow-xl py-2 z-10 border border-primary-700">
                    <Link
                      href="/profile"
                      className="flex items-center px-4 py-2.5 text-sm text-white hover:bg-primary-700 transition-colors duration-200 font-medium"
                      onClick={() => setShowDropdown(false)}
                    >
                      <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Profile
                    </Link>
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        handleSignOut();
                      }}
                      className="flex items-center w-full text-left px-4 py-2.5 text-sm text-white hover:bg-primary-700 transition-colors duration-200 font-medium"
                    >
                      <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                href="/login"
                className="hidden md:flex items-center space-x-2 text-white hover:text-yellow-300 transition-colors duration-200 font-medium"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                <span>Login</span>
              </Link>
            )}

            <button className="md:hidden text-white hover:text-yellow-300 transition-colors duration-200">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
